import { useModularBenefitsDocumentSigningPrepareCreate } from '@ben/api/gen/codegen';
import { useFormatDate } from '@ben/common/date';
import RiEditLine from '@ben/icons/edit-line';
import RiExternalLinkLine from '@ben/icons/external-link-line';
import RiFileTextLine from '@ben/icons/file-text-line';
import Banner from '@ben/orion/banner';
import Button from '@ben/orion/button';
import { RIcon } from '@ben/orion/remix-icon/remix-icon';
import { TruncatedText } from '@ben/orion/truncated-text';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { DocusealSigningModal } from '@ben/shared/components/docuseal-signing-modal';
import { MAX_VISIBLE_DOCUMENT_SIGNING_HISTORY_RECORDS } from '@ben/shared/constants';
import type { DocumentSigningValue } from '../../../types/modular-benefit';
import {
  getDocumentSigningSummaryVisibility,
  mergeDocumentSigningDisplayHistory,
} from '@ben/shared/utils/document-signing-selection';
import {
  buildCompletedDocumentSigningValue,
  getActiveDocumentSigningValue,
  isEmployeeDocumentSigningCompleted,
} from '@ben/shared/utils/document-signing';
import { useDisclosure } from '@mantine/hooks';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled, { css, useTheme } from 'styled-components';
import { DocumentSigningSelectionDisplay } from '../../document-signing-selection-display';

const SIGNED_DOCUMENTS_HISTORY_LIST_ID = 'signed-documents-history-list';

interface DocumentSigningProps {
  id?: number;
  name?: string;
  description?: string;
  disabled?: boolean;
  benefitId?: number;
  benefitForm?: Record<string, unknown>;
  value?: DocumentSigningValue | null;
  allSigningRequests?: DocumentSigningValue[];
  needsResigning?: boolean;
  hasChanges?: boolean;
  initialSigningValue?: unknown;
  onChange?: (value: DocumentSigningValue) => void;
}

const DocumentSigning = ({
  id,
  name,
  description,
  disabled = false,
  benefitId,
  benefitForm,
  value,
  allSigningRequests = [],
  needsResigning = false,
  hasChanges = false,
  initialSigningValue,
  onChange,
}: DocumentSigningProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const formatDate = useFormatDate();
  const [opened, { open, close }] = useDisclosure(false);
  const [isHistoryExpanded, { toggle: toggleHistoryExpanded }] =
    useDisclosure(false);
  const [embedSource, setEmbedSource] = useState<string>('');
  const [signingRequestId, setSigningRequestId] = useState<number | null>(null);

  const { mutateAsync: prepareSigning, isPending } =
    useModularBenefitsDocumentSigningPrepareCreate();

  const contentDescription =
    description || t('benefit.documentSigning.description');

  const configSigningValue = getActiveDocumentSigningValue(allSigningRequests);
  const needsConfigFallback =
    !needsResigning &&
    value != null &&
    !isEmployeeDocumentSigningCompleted(value);
  const effectiveValue = needsConfigFallback
    ? (configSigningValue ?? value)
    : value;

  const signingHistory = mergeDocumentSigningDisplayHistory(
    allSigningRequests,
    value
  );
  const {
    summarySigningHistory,
    signingValueUnchanged,
    showUnsavedSignedOnly,
    showSummary,
  } = getDocumentSigningSummaryVisibility({
    signingHistory,
    currentSigningValue: value,
    initialSigningValue,
    hasChanges,
    needsResigning,
    hideSummaryWhenHasChangesAndUnchanged: true,
  });

  if (showUnsavedSignedOnly) {
    return (
      <SummaryWrapper>
        <Typography variant={TypographyVariant.bodySm2}>{name}</Typography>
        <Typography variant={TypographyVariant.bodyMd1}>
          {t('benefitDetails.selections.signedByYou')}
        </Typography>
      </SummaryWrapper>
    );
  }

  if (showSummary) {
    return (
      <SummaryWrapper>
        <Typography variant={TypographyVariant.bodySm2}>{name}</Typography>
        <DocumentSigningSelectionDisplay
          componentName={name ?? ''}
          signingHistory={summarySigningHistory}
        />
      </SummaryWrapper>
    );
  }

  const signedDocuments = allSigningRequests.filter(
    (request) =>
      request.signedDocumentUrl &&
      request.signingRequestId !== effectiveValue?.signingRequestId
  );
  const shouldShowHistoryToggle =
    signedDocuments.length > MAX_VISIBLE_DOCUMENT_SIGNING_HISTORY_RECORDS;
  const visibleSignedDocuments = isHistoryExpanded
    ? signedDocuments
    : signedDocuments.slice(0, MAX_VISIBLE_DOCUMENT_SIGNING_HISTORY_RECORDS);

  const handleSignClick = async (): Promise<void> => {
    if (!id || !benefitId) return;

    try {
      const response = await prepareSigning({
        benefitPk: benefitId,
        data: {
          componentId: id,
          userInput: benefitForm ?? {},
        },
      });

      setSigningRequestId(response.signingRequestId);
      setEmbedSource(response.embedSrc);
      open();
    } catch {
      toast.error(t('benefit.documentSigning.prepareError'));
    }
  };

  const handleSignButtonClick = (): void => {
    void handleSignClick();
  };

  const handleSigningComplete = (): void => {
    close();
    setEmbedSource('');
    onChange?.(buildCompletedDocumentSigningValue(value, signingRequestId));
    setSigningRequestId(null);
  };

  return (
    <>
      <Container>
        <Header>
          <TitleRow>
            <RIcon
              icon={RiEditLine}
              size="md"
              color={theme.palette.primary.main}
              aria-hidden
            />
            <Typography variant={TypographyVariant.h2}>
              {t('benefit.documentSigning.title')}
            </Typography>
          </TitleRow>
          <TitleDivider />
        </Header>

        <Description variant={TypographyVariant.bodyMd1}>
          {contentDescription}
        </Description>

        {needsResigning && (
          <Banner
            variant="information"
            message={t('benefit.documentSigning.resignRequiredMessage')}
          />
        )}

        <Card>
          <FileDetails>
            <FileIconWrapper>
              <RIcon
                icon={RiFileTextLine}
                size="md"
                color={theme.palette.success.main}
                aria-hidden
              />
            </FileIconWrapper>

            <FileTextContainer>
              <Typography
                color={theme.palette.neutral[70]}
                variant={TypographyVariant.bodySm1}
              >
                {t('benefit.documentSigning.fileTypeLabel')}
              </Typography>
              <TruncatedText
                color={theme.palette.neutral[90]}
                variant={TypographyVariant.bodyMd2}
              >
                {name}
              </TruncatedText>
            </FileTextContainer>
          </FileDetails>

          <Button
            variant="primary"
            onClick={handleSignButtonClick}
            loading={isPending}
            disabled={disabled || !benefitId || !id}
            aria-label={t('benefit.documentSigning.signButtonAriaLabel')}
            style={{ minWidth: '112px' }}
          >
            {t('benefit.documentSigning.signButton')}
          </Button>
        </Card>

        {signedDocuments.length > 0 &&
          !(hasChanges && signingValueUnchanged) && (
            <>
              <SignedDocumentsList
                id={SIGNED_DOCUMENTS_HISTORY_LIST_ID}
                aria-label={t(
                  'benefit.documentSigning.signedDocumentsListAriaLabel'
                )}
              >
                {visibleSignedDocuments.map((request) => {
                  const dateLabel = formatDate(request.signedAt);
                  const hasDate = dateLabel && dateLabel !== '-';
                  const linkLabel = hasDate
                    ? t('benefit.documentSigning.signedDocumentLinkLabel', {
                        date: dateLabel,
                        name,
                      })
                    : (name ?? '');
                  return (
                    <li key={request.signingRequestId}>
                      <Button
                        variant="text"
                        component="a"
                        href={request.signedDocumentUrl ?? ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        aria-label={linkLabel}
                        rightSection={
                          <RIcon
                            icon={RiExternalLinkLine}
                            size="sm"
                            aria-hidden
                          />
                        }
                      >
                        {linkLabel}
                      </Button>
                    </li>
                  );
                })}
              </SignedDocumentsList>
              {shouldShowHistoryToggle && (
                <Button
                  variant="text"
                  type="button"
                  onClick={toggleHistoryExpanded}
                  aria-expanded={isHistoryExpanded}
                  aria-controls={SIGNED_DOCUMENTS_HISTORY_LIST_ID}
                >
                  {isHistoryExpanded
                    ? t('words.showLess')
                    : t('words.showMore')}
                </Button>
              )}
            </>
          )}
      </Container>

      <DocusealSigningModal
        opened={opened}
        onClose={close}
        title={t('benefit.documentSigning.title')}
        closeAriaLabel={t('benefit.documentSigning.closeModalAriaLabel')}
        embedSource={embedSource}
        onComplete={handleSigningComplete}
      />
    </>
  );
};

export default DocumentSigning;

const Container = styled.section`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    padding: ${theme.spacing(4, 3)};
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: ${theme.borderRadius.sm};
  `}
`;

const Header = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `}
`;

const TitleRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
  `}
`;

const TitleDivider = styled.div`
  ${({ theme }) => css`
    width: 100%;
    border-bottom: 1px dashed ${theme.palette.neutral[60]};
  `}
`;

const Description = styled(Typography)`
  max-width: 740px;
  text-align: left;
`;

const Card = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
    border: 1px solid ${theme.palette.neutral[50]};
    border-radius: ${theme.spacing(1)};
    padding: ${theme.spacing(1)};
    background: ${theme.palette.neutral['00']};
  `}
`;

const FileDetails = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
  `}
`;

const FileIconWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: ${theme.spacing(1)};
    background: ${theme.palette.primary.bg};
  `}
`;

const FileTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SignedDocumentsList = styled.ul`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(0.5)};
    list-style: none;
    margin: 0;
    padding: 0;
  `}
`;

const SummaryWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;
